// Kho kịch bản (gói F): bảng kịch bản mới nhất mỗi tên (tên, phiên bản, mã bản ngắn, từ bot, ngày, số tham số), Đẩy xuống… (tick nhiều bot,
// kết quả pushed/queued/failed), Chi tiết (mô tả, số bước, tham số), super: Sao chép sang Boss… Cập nhật tức thời: bundles.changed → tải lại.
import { api, errMsg } from "../api";
import { busy, clear, confirmBox, gen, h, infoBox, toast } from "../dom";
import { fmtTime } from "../format";
import { botName, isSuper, loadBots, sortBots, store } from "../store";
import type { Bot } from "../types";
import { uiSocket } from "../ws";
import { ganTenKbChu, tenKb, tenKbDaBiet } from "../ten-kb"; // L-78: hiện TÊN kịch bản thay vì mã kb-…

// Đối tượng Bundle theo docs/boss-api.md (kho, không phải Bundle của extension).
export interface BundleParam {
  type?: string;
  default?: string | number | boolean;
  required?: boolean;
  label?: string;
}
export interface KhoBundle {
  id: string;
  ownerId: string;
  name: string;
  hash: string;
  version: number;
  fromBotId: string | null;
  fromBotName?: string | null;
  createdAt: string;
  params: Record<string, BundleParam>;
  description?: string;
}
export interface KhoBundleFull extends KhoBundle {
  json: { steps?: unknown[]; [k: string]: unknown } | null;
}
export interface PushResult {
  skipped?: string[];
  pushed: string[];
  queued: string[];
  failed: { botId: string; reason: string }[];
}

export const shortHash = (hash: string): string => hash.slice(0, 10);

// Đếm bước kể cả bước lồng (steps[].steps / children) — đủ cho hiển thị.
export function countSteps(steps: unknown): number {
  if (!Array.isArray(steps)) return 0;
  let n = 0;
  for (const s of steps) {
    n++;
    if (s && typeof s === "object") {
      const o = s as Record<string, unknown>;
      for (const k of ["steps", "then", "else", "each"]) if (Array.isArray(o[k])) n += countSteps(o[k]);
    }
  }
  return n;
}

export async function renderBundles(root: HTMLElement): Promise<() => void> {
  const alive = gen("bundles");
  root.append(h("h1", {}, "Kho kịch bản"));
  const info = h("p", { class: "muted small", "data-testid": "bundles-info" }, "Đang tải…");
  const tbody = h("tbody", { "data-testid": "bundles-body" });
  // N9: cột "Mã bản" (hash) chỉ cho Super (chế độ kỹ thuật) — người dùng thường phân biệt bản bằng "v3" + ngày.
  const kt = isSuper();
  const tbl = h("div", { class: "tbl" }, h("table", { "data-testid": "bundles-table" },
    h("thead", {}, h("tr", {}, h("th", {}, "Tên"), h("th", { title: "Số lần bản này được lấy lên kho (tăng mỗi lần lấy bản khác)" }, "Phiên bản"), kt ? h("th", { title: "Mã bản (hash) rút gọn — hai bản cùng mã là giống hệt nhau" }, "Mã bản") : null, h("th", { title: "Bot đã cung cấp bản này" }, "Từ bot"), h("th", { title: "Lúc lấy lên kho" }, "Ngày"), h("th", { title: "Số tham số kịch bản cần khi chạy" }, "Tham số"), h("th", {}))),
    tbody));
  const empty = h("div", { class: "empty card", hidden: true, "data-testid": "bundles-empty" }, "Kho trống. Ở trang ", h("a", { href: "#/bots", title: "Về trang Bot" }, "Bot"), ", mở mục kịch bản của một bot trực tuyến và bấm ", h("b", {}, "Lấy lên kho"), ".");
  root.append(h("p", { class: "small muted" }, "Kịch bản lấy lên từ một bot có thể đẩy xuống các bot khác (giữ nguyên tên)."), info, empty, tbl);

  let list: KhoBundle[] = [];
  async function load(): Promise<void> {
    const [bundles] = await Promise.all([api<KhoBundle[]>("GET", "/api/bundles"), loadBots().catch(() => [])]);
    if (!alive()) return;
    list = bundles;
    paint();
  }
  function paint(): void {
    clear(tbody);
    info.textContent = list.length ? `${list.length} kịch bản (bản mới nhất mỗi tên)` : "";
    empty.hidden = list.length > 0;
    tbl.hidden = list.length === 0;
    for (const b of list) {
      const nParams = Object.keys(b.params ?? {}).length;
      const from = b.fromBotName ?? (b.fromBotId ? botName(b.fromBotId) : "—");
      tbody.append(h("tr", { "data-testid": "bundle-row", dataset: { name: b.name, id: b.id } },
        h("td", { style: "min-width:160px" }, h("b", { class: "wrap", "data-testid": "bundle-name" }, tenKb(b.name, { lienKet: true })), isSuper() && b.ownerId !== store.user?.id ? h("div", { class: "small muted", title: "Tài khoản Boss sở hữu kịch bản này" }, "chủ: ", b.ownerId) : null),
        h("td", { "data-testid": "bundle-version" }, "v", String(b.version)),
        kt ? h("td", {}, h("code", { title: `Mã bản (hash) đầy đủ: ${b.hash}`, "data-testid": "bundle-hash" }, shortHash(b.hash))) : null,
        h("td", { class: "wrap" }, from),
        h("td", { class: "nowrap" }, fmtTime(b.createdAt)),
        h("td", { "data-testid": "bundle-nparams" }, String(nParams)),
        h("td", { class: "nowrap" },
          h("button", { type: "button", class: "mini", title: "Xem mô tả, số bước và các tham số của kịch bản này", "data-testid": "bundle-detail", onclick: (e: Event) => void detail(b, e.currentTarget as HTMLButtonElement) }, "Chi tiết"),
          " ",
          h("button", { type: "button", class: "mini pri", title: "Chọn các bot để cài bản kịch bản này xuống (bot ngoại tuyến hoặc đang bận sẽ xếp hàng)", "data-testid": "bundle-push", onclick: () => void pushDialog(b) }, "Đẩy xuống…"),
          " ",
          h("button", { type: "button", class: "mini", title: "Một nút: đẩy bản này xuống MỌI bot của tài khoản sở hữu (bỏ bot đã có đúng bản; bot ngoại tuyến/bận xếp hàng). Học ở một máy → mọi máy cùng có.", "data-testid": "bundle-push-all", onclick: (e: Event) => void pushAll(b, e.currentTarget as HTMLButtonElement) }, "Phát cho tất cả"),
          isSuper() ? [" ", h("button", { type: "button", class: "mini", title: "Chỉ Super Boss: chép bản kịch bản này vào kho của một tài khoản Boss khác", "data-testid": "bundle-copy", onclick: () => void copyDialog(b) }, "Sao chép sang Boss…")] : null)));
    }
  }

  async function copyDialog(b: KhoBundle): Promise<void> {
    // Chưa có API danh sách user ở gói này → gợi ý các chủ bot đã thấy + ô nhập id tài khoản.
    const owners = new Map<string, string>();
    for (const x of store.bots.values()) if (x.ownerId !== b.ownerId) owners.set(x.ownerId, x.ownerName);
    const sel = h("select", { title: "Chọn tài khoản Boss sẽ nhận bản sao", "data-testid": "copy-owner" }, h("option", { value: "" }, "— chọn Boss đích —"), ...[...owners].map(([id, name]) => h("option", { value: id }, `${name} (${id})`)));
    const manual = h("input", { placeholder: "hoặc nhập mã tài khoản đích", title: "Mã (id) tài khoản đích nếu không có trong danh sách; ưu tiên hơn ô chọn", "data-testid": "copy-owner-id" });
    const err = h("div", { class: "err-box", hidden: true });
    await new Promise<void>((resolve) => {
      const go = h("button", { type: "button", class: "pri", title: "Chép bản kịch bản này vào kho của Boss đích (bản gốc giữ nguyên)", "data-testid": "copy-go" }, "Sao chép");
      const wrap = h("div", { class: "modal-wrap", role: "dialog", "aria-modal": "true", "data-testid": "copy-dialog" },
        h("div", { class: "modal" },
          (() => { const t = h("h3", {}, `Sao chép “${tenKbDaBiet(b.name)}” (v${b.version}) sang Boss khác`); ganTenKbChu(t, b.name, (ten) => `Sao chép “${ten}” (${b.name}, v${b.version}) sang Boss khác`); return t; })(),
          h("label", { class: "f" }, h("span", {}, "Boss đích"), sel),
          h("label", { class: "f" }, h("span", {}, "Mã tài khoản (nếu không có trong danh sách)"), manual),
          err,
          h("div", { class: "row end" }, h("button", { type: "button", title: "Đóng hộp, không sao chép", onclick: () => { wrap.remove(); resolve(); } }, "Huỷ"), go)));
      go.addEventListener("click", () => void busy(go, "Đang chép…", async () => {
        const to = manual.value.trim() || sel.value;
        err.hidden = true;
        if (!to) { err.textContent = "Chưa chọn Boss đích"; err.hidden = false; return; }
        try {
          const r = await api<KhoBundle>("POST", `/api/bundles/${encodeURIComponent(b.id)}/copy`, { toOwnerId: to });
          toast(`Đã sao chép “${tenKbDaBiet(r.name)}” sang ${to} (v${r.version})`);
          wrap.remove();
          resolve();
          await load();
        } catch (e) { err.textContent = errMsg(e); err.hidden = false; }
      }));
      document.body.appendChild(wrap);
    });
  }

  await load();
  const off = uiSocket.on((ev) => {
    if (ev.type === "bundles.changed") void load().catch((e) => toast(errMsg(e), "err"));
  });
  return () => off();
}

// Lấy kịch bản từ bot lên kho — dùng ở thẻ bot (pages/bots.ts). Nút bận do nơi gọi lo: thẻ bot giữ trạng thái đang gửi
// theo khoá (guiTuThe) để thẻ vẽ lại giữa chừng không mở khoá nút.
export async function pullToKho(bot: Bot, name: string): Promise<void> {
  try {
    const r = await api<KhoBundle>("POST", "/api/bundles/pull", { botId: bot.id, name });
    // N11: Kho kịch bản đã gộp vào Kịch bản — bản vừa lưu hiện ở phần "Kịch bản học trên bot" của trang Kịch bản.
    toast(`Đã lấy “${tenKbDaBiet(r.name)}” lên kho (v${r.version}${isSuper() ? `, mã bản ${shortHash(r.hash)}` : ""}) — mở ở thẻ bot: Kịch bản của bot › Mở`);
  } catch (e) { toast(errMsg(e), "err"); }
}

// N11 (05/10/2026): ba hộp dưới dùng chung cho Kho bundle (Chế độ kỹ thuật) và trang Kịch bản học trên bot (pages/kich-ban-tu-bot.ts).
export async function pushAll(b: KhoBundle, btn: HTMLButtonElement): Promise<void> {
  // Hộp xác nhận tự vẽ như mọi nơi khác (02/10/2026: trước dùng window.confirm của trình duyệt — khác giao diện, không đóng theo Esc/đổi trang).
  if (btn.disabled) return;
  const ok = await confirmBox({ title: "Phát cho tất cả bot?", text: `Phát “${tenKbDaBiet(b.name)}” (v${b.version}) cho TẤT CẢ bot của tài khoản sở hữu? Bot đã có đúng bản này sẽ được bỏ qua; bot ngoại tuyến hoặc đang bận sẽ xếp hàng.`, okText: "Phát cho tất cả", okTitle: "Đẩy bản này xuống mọi bot của tài khoản sở hữu" });
  if (!ok) return;
  await busy(btn, "Đang phát…", async () => {
    try {
      const r = await api<PushResult>("POST", `/api/bundles/${encodeURIComponent(b.id)}/push-all`, {});
      const sk = r.skipped?.length ?? 0;
      toast(`Phát “${tenKbDaBiet(b.name)}”: ${r.pushed.length} đã nhận, ${r.queued.length} xếp hàng, ${sk} đã có sẵn${r.failed.length ? `, ${r.failed.length} thất bại (${r.failed.map((f) => `${botName(f.botId)}: ${f.reason}`).join("; ")})` : ""}`, r.failed.length ? "warn" : "info");
    } catch (e) { toast(errMsg(e), "err"); }
  });
}

export async function detail(b: KhoBundle, btn: HTMLButtonElement): Promise<void> {
  await busy(btn, "…", async () => {
    let full: KhoBundleFull;
    let others: KhoBundle[] = [];
    try {
      full = await api<KhoBundleFull>("GET", `/api/bundles/${encodeURIComponent(b.id)}`);
      // Các bản cũ cùng tên (cùng chủ) — để đẩy lại bản trước khi bản mới hỏng.
      others = (await api<KhoBundle[]>("GET", "/api/bundles?all=1")).filter((x) => x.name === b.name && x.ownerId === b.ownerId && x.id !== b.id).sort((x, y) => y.version - x.version);
    } catch (e) { toast(errMsg(e), "err"); return; }
    const params = Object.entries(full.params ?? {});
    const ten = tenKbDaBiet(full.name);
    const banKhac = h("div", { "data-testid": "bundle-versions" },
      h("h3", {}, `Các bản khác của “${ten}” (${others.length})`),
      others.length
        ? h("div", { class: "tbl" }, h("table", {}, h("thead", {}, h("tr", {}, h("th", {}, "Phiên bản"), isSuper() ? h("th", {}, "Mã bản") : null, h("th", {}, "Từ bot"), h("th", {}, "Ngày"), h("th", {}))),
          h("tbody", {}, ...others.map((o) => h("tr", { "data-testid": "bundle-version-row", dataset: { version: String(o.version) } },
            h("td", {}, `v${o.version}`), isSuper() ? h("td", {}, h("code", { title: `Mã bản đầy đủ: ${o.hash}` }, shortHash(o.hash))) : null, h("td", { class: "wrap" }, o.fromBotName ?? (o.fromBotId ? botName(o.fromBotId) : "—")), h("td", { class: "nowrap" }, fmtTime(o.createdAt)),
            h("td", {}, h("button", { type: "button", class: "mini", title: `Đẩy lại bản v${o.version} xuống các bot (dùng khi bản mới hỏng)`, "data-testid": "bundle-version-push", onclick: () => void pushDialog(o) }, "Đẩy bản này…")))))))
        : h("p", { class: "muted small" }, "Chưa có bản nào khác — lấy lên kho lần nữa sau khi dạy lại sẽ tạo bản mới, bản cũ vẫn giữ để quay lại."));
    await infoBox(`${ten} — v${full.version}`, h("div", { "data-testid": "bundle-detail-box" },
      isSuper() ? h("p", { class: "small muted wrap" }, "mã bản ", h("code", { title: "Mã bản (hash) đầy đủ của kịch bản" }, full.hash)) : null,
      full.description ? h("p", { class: "wrap", "data-testid": "bundle-desc" }, full.description) : h("p", { class: "muted small" }, "Không có mô tả."),
      h("p", { class: "small" }, h("b", { "data-testid": "bundle-steps" }, String(countSteps(full.json?.steps))), " bước · từ bot ", full.fromBotName ?? (full.fromBotId ? botName(full.fromBotId) : "—"), " · ", fmtTime(full.createdAt)),
      params.length
        ? h("div", { class: "tbl" }, h("table", {}, h("thead", {}, h("tr", {}, h("th", {}, "Tham số"), h("th", { title: "Kiểu dữ liệu tham số" }, "Kiểu"), h("th", { title: "Giá trị dùng khi bạn không nhập" }, "Mặc định"), h("th", { title: "có = phải nhập khi phát lệnh" }, "Bắt buộc"))),
          h("tbody", { "data-testid": "bundle-params" }, ...params.map(([k, p]) => h("tr", {},
            h("td", {}, h("code", {}, k), p.label ? h("div", { class: "small muted wrap" }, p.label) : null),
            h("td", {}, p.type === "number" ? "số" : p.type === "boolean" ? "đúng/sai" : p.type && p.type !== "string" ? p.type : "chữ"),
            h("td", { class: "wrap" }, p.default === undefined ? h("span", { class: "muted" }, "—") : String(p.default)),
            h("td", {}, p.required ? "có" : "không"))))))
        : h("p", { class: "muted small" }, "Kịch bản không có tham số."),
      banKhac));
  });
}

export async function pushDialog(b: KhoBundle): Promise<void> {
  let bots: Bot[];
  try { bots = sortBots(await loadBots(true)); } catch (e) { toast(errMsg(e), "err"); return; }
  const mine = bots.filter((x) => isSuper() || x.ownerId === b.ownerId);
  const boxes = new Map<string, HTMLInputElement>();
  const rows = mine.map((x) => {
    const has = x.bundles.find((r) => r.name === b.name);
    const same = has?.hash === b.hash;
    const cb = h("input", { type: "checkbox", disabled: same, title: same ? "Bot đã có đúng bản này — không cần đẩy" : "Tick = đẩy bản kịch bản này xuống bot", "data-testid": "push-bot", dataset: { botId: x.id } });
    boxes.set(x.id, cb);
    return h("label", { class: `inline${same ? " locked" : ""}`, style: "display:flex;gap:6px;align-items:center", title: same ? "Bot đã có đúng bản này — không cần đẩy" : "Tick = đẩy bản kịch bản này xuống bot" }, cb,
      h("span", { class: "grow wrap" }, x.name),
      x.online ? null : h("span", { class: "tag", title: "Bot đang ngoại tuyến: sẽ xếp hàng và tự đẩy khi bot trực tuyến" }, "ngoại tuyến — chờ trực tuyến"),
      x.current ? h("span", { class: "tag warn", title: "Bot đang chạy dở — sẽ đẩy sau khi lần chạy xong" }, "đang bận — xếp hàng") : null,
      same ? h("span", { class: "tag ok", title: "Bot đã có đúng bản này" }, "đã có bản này") : has ? h("span", { class: "tag", title: `Bot đang có bản khác cùng tên — đẩy sẽ thay bằng bản trong kho (mã bản ${shortHash(has.hash)})` }, isSuper() ? `có bản khác (${shortHash(has.hash)})` : "có bản khác") : null);
  });
  const result = h("div", { hidden: true, "data-testid": "push-result" });
  const err = h("div", { class: "err-box", hidden: true });
  await new Promise<void>((resolve) => {
    const go = h("button", { type: "button", class: "pri", title: "Gửi bản kịch bản này tới các bot đã tick; bot trực tuyến rảnh nhận ngay, còn lại xếp hàng", "data-testid": "push-go" }, "Đẩy xuống");
    const closeBtn = h("button", { type: "button", title: "Đóng hộp này", "data-testid": "push-close", onclick: () => { wrap.remove(); resolve(); } }, "Đóng");
    const wrap = h("div", { class: "modal-wrap", role: "dialog", "aria-modal": "true", "data-testid": "push-dialog" },
      h("div", { class: "modal" },
        (() => { const t = h("h3", {}, `Đẩy “${tenKbDaBiet(b.name)}” (v${b.version}) xuống bot`); ganTenKbChu(t, b.name, (ten) => `Đẩy “${ten}” (v${b.version}) xuống bot`); return t; })(),
        rows.length ? h("div", { style: "display:flex;flex-direction:column;gap:4px" }, ...rows) : h("p", { class: "muted" }, "Chưa có bot nào."),
        h("p", { class: "small muted" }, "Bot ngoại tuyến hoặc đang chạy sẽ được xếp hàng và tự đẩy khi rảnh."),
        result, err,
        h("div", { class: "row end" }, closeBtn, go)));
    go.addEventListener("click", () => void busy(go, "Đang đẩy…", async () => {
      const botIds = [...boxes].filter(([, cb]) => cb.checked).map(([id]) => id);
      err.hidden = true;
      if (!botIds.length) { err.textContent = "Chưa tick bot nào"; err.hidden = false; return; }
      try {
        const r = await api<PushResult>("POST", `/api/bundles/${encodeURIComponent(b.id)}/push`, { botIds });
        clear(result);
        result.hidden = false;
        const line = (cls: string, label: string, ids: string[], extra?: (id: string) => string) => ids.length
          ? h("div", { class: cls, "data-testid": `push-${cls}` }, `${label}: `, ids.map((id) => `${botName(id)}${extra ? ` (${extra(id)})` : ""}`).join(", "))
          : null;
        for (const el of [
          line("pushed", "Đã đẩy", r.pushed),
          line("queued", "Xếp hàng (đẩy khi bot rảnh/trực tuyến)", r.queued),
          line("failed", "Thất bại", r.failed.map((f) => f.botId), (id) => r.failed.find((f) => f.botId === id)?.reason ?? ""),
        ]) if (el) result.append(el);
        result.querySelector(".pushed")?.classList.add("ok");
        result.querySelector(".queued")?.classList.add("warn");
        result.querySelector(".failed")?.classList.add("err");
        toast(r.failed.length ? `Đẩy xong: ${r.pushed.length} thành công, ${r.queued.length} xếp hàng, ${r.failed.length} thất bại` : `Đã đẩy ${r.pushed.length} bot, xếp hàng ${r.queued.length}`, r.failed.length ? "warn" : "info");
        for (const id of [...r.pushed]) { const cb = boxes.get(id); if (cb) { cb.checked = false; cb.disabled = true; } }
      } catch (e) { err.textContent = errMsg(e); err.hidden = false; }
    }));
    document.body.appendChild(wrap);
  });
}
